import { useState, useEffect, useCallback } from 'react'
import { AdminAlert, AdminComingSoon, isNotBuiltYet } from '../AdminShared'
import { Skeleton } from '../components/Skeleton'
import {
  adminGetCampaigns,
  adminCreateCampaign,
  adminUpdateCampaign,
  adminUpdateCampaignPrice,
  adminActivateCampaign,
  adminPauseCampaign,
  adminEndCampaign,
  adminGetCampaignStats,
  adminGetPayoutExport,
  adminGetClaims,
  adminRejectClaim,
  adminMarkClaimPaid,
  adminGetBountyFlags,
  adminBanBountyAccount,
  adminUnbanBountyAccount,
} from '../../../services/bounty'

const TASK_TYPES = [
  { value: 'twitter_repost', label: 'X/Twitter Repost' },
  { value: 'referral', label: 'Referral' },
  { value: 'custom', label: 'Custom' },
]

const REWARD_MODES = [
  { value: 'cpt_fixed', label: 'Fixed CPT amount' },
  { value: 'usd_value', label: 'USD value (converted via CPT price)' },
]

const CAMPAIGN_STATUS_FILTERS = [
  { key: undefined, label: 'All' },
  { key: 'draft', label: 'Draft' },
  { key: 'active', label: 'Active' },
  { key: 'paused', label: 'Paused' },
  { key: 'ended', label: 'Ended' },
]

const CLAIM_STATUS_FILTERS = [
  { key: undefined, label: 'All' },
  { key: 'eligible', label: 'Eligible' },
  { key: 'claimed', label: 'Claimed' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'blocked', label: 'Blocked' },
]

function fmt(n) {
  const num = Number(n)
  return Number.isFinite(num) ? num.toLocaleString() : (n ?? '—')
}

function toLocalInput(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
}

function Dialog({ title, children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-on-background/60 p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className={`bg-surface border-4 border-on-background w-full ${wide ? 'max-w-lg' : 'max-w-sm'} flex flex-col gap-4 p-6 max-h-[90vh] overflow-y-auto`}
        style={{ boxShadow: '6px 6px 0px 0px var(--neo-border-color)' }}>
        <div className="flex items-center justify-between">
          <h2 className="font-extrabold text-[14px] uppercase tracking-widest">{title}</h2>
          <button onClick={onClose}>
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>
        {children}
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">{label}</label>
      {children}
    </div>
  )
}

const inputCls = "bg-surface border-2 border-on-background px-3 py-2 text-[13px] focus:outline-none focus:border-primary-container"

function CampaignFormDialog({ campaign, onClose, onSaved }) {
  const isEdit = !!campaign
  const [form, setForm] = useState({
    title: campaign?.title ?? '',
    description: campaign?.description ?? '',
    taskType: campaign?.taskType ?? 'custom',
    targetUrl: campaign?.targetUrl ?? '',
    rewardMode: campaign?.rewardMode ?? 'cpt_fixed',
    rewardAmountCpt: campaign?.rewardAmountCpt ?? '',
    rewardValueUsd: campaign?.rewardValueUsd ?? '',
    cptPriceUsd: campaign?.cptPriceUsd ?? '',
    totalPoolCpt: campaign?.totalPoolCpt ?? '',
    maxClaimsPerUser: campaign?.maxClaimsPerUser ?? 1,
    requireEmailVerified: campaign?.requireEmailVerified ?? true,
    requireKyc: campaign?.requireKyc ?? false,
    campaignStart: toLocalInput(campaign?.campaignStart) || toLocalInput(new Date().toISOString()),
    campaignEnd: toLocalInput(campaign?.campaignEnd),
    claimStart: toLocalInput(campaign?.claimStart),
    claimEnd: toLocalInput(campaign?.claimEnd),
    autoCreditOnEligibility: campaign?.autoCreditOnEligibility ?? true,
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const set = (key) => (e) => {
    const val = e?.target?.type === 'checkbox' ? e.target.checked : e.target.value
    setForm(f => ({ ...f, [key]: val }))
  }

  const save = async () => {
    if (!form.title.trim() || !form.description.trim() || !form.totalPoolCpt) {
      setError('Title, description and total pool are required')
      return
    }
    if (form.taskType === 'twitter_repost' && !form.targetUrl.trim()) {
      setError('Target URL is required for repost campaigns')
      return
    }
    if (form.rewardMode === 'cpt_fixed' && !form.rewardAmountCpt) {
      setError('CPT reward amount is required')
      return
    }
    if (form.rewardMode === 'usd_value' && (!form.rewardValueUsd || !form.cptPriceUsd)) {
      setError('USD value and current CPT price are both required')
      return
    }
    if (!form.campaignStart) {
      setError('Campaign start is required')
      return
    }

    const base = {
      title: form.title.trim(),
      description: form.description.trim(),
      totalPoolCpt: Number(form.totalPoolCpt),
      maxClaimsPerUser: Number(form.maxClaimsPerUser) || 1,
      requireEmailVerified: !!form.requireEmailVerified,
      requireKyc: !!form.requireKyc,
      campaignStart: new Date(form.campaignStart).toISOString(),
      campaignEnd: form.campaignEnd ? new Date(form.campaignEnd).toISOString() : undefined,
      claimStart: form.claimStart ? new Date(form.claimStart).toISOString() : undefined,
      claimEnd: form.claimEnd ? new Date(form.claimEnd).toISOString() : undefined,
      autoCreditOnEligibility: !!form.autoCreditOnEligibility,
      rewardAmountCpt: form.rewardMode === 'cpt_fixed' ? Number(form.rewardAmountCpt) : undefined,
      rewardValueUsd: form.rewardMode === 'usd_value' ? Number(form.rewardValueUsd) : undefined,
      cptPriceUsd: form.rewardMode === 'usd_value' ? Number(form.cptPriceUsd) : undefined,
    }
    // taskType/rewardMode are locked after creation (backend rejects them on update).
    const payload = isEdit ? base : { ...base, taskType: form.taskType, rewardMode: form.rewardMode, targetUrl: form.targetUrl.trim() || undefined }

    setLoading(true)
    setError(null)
    try {
      if (isEdit) await adminUpdateCampaign(campaign.id, payload)
      else await adminCreateCampaign(payload)
      onSaved()
    } catch (err) {
      setError(err.message ?? 'Failed to save campaign')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog title={isEdit ? 'Edit Campaign' : 'Create Campaign'} onClose={onClose} wide>
      {error && <AdminAlert type="error" message={error} />}

      <Field label="Title">
        <input className={inputCls} value={form.title} onChange={set('title')} />
      </Field>

      <Field label="Description">
        <textarea rows={2} className={`${inputCls} resize-y`} value={form.description} onChange={set('description')} />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Task type">
          <select className={inputCls} value={form.taskType} onChange={set('taskType')} disabled={isEdit}>
            {TASK_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </Field>
        <Field label="Reward mode">
          <select className={inputCls} value={form.rewardMode} onChange={set('rewardMode')} disabled={isEdit}>
            {REWARD_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </Field>
      </div>

      {form.taskType === 'twitter_repost' && (
        <Field label="Target post URL">
          <input className={inputCls} placeholder="https://x.com/chicagonews/status/..." value={form.targetUrl} onChange={set('targetUrl')} disabled={isEdit} />
        </Field>
      )}

      {form.rewardMode === 'cpt_fixed' ? (
        <Field label="Reward (CPT per claim)">
          <input type="number" min="1" className={inputCls} value={form.rewardAmountCpt} onChange={set('rewardAmountCpt')} />
        </Field>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Reward value (USD per claim)">
            <input type="number" min="0.01" step="0.01" className={inputCls} value={form.rewardValueUsd} onChange={set('rewardValueUsd')} />
          </Field>
          <Field label="Current CPT price (USD)">
            <input type="number" min="0.000001" step="any" className={inputCls} value={form.cptPriceUsd} onChange={set('cptPriceUsd')} />
          </Field>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Field label="Total CPT pool">
          <input type="number" min="1" className={inputCls} value={form.totalPoolCpt} onChange={set('totalPoolCpt')} />
        </Field>
        <Field label="Max claims per user">
          <input type="number" min="1" className={inputCls} value={form.maxClaimsPerUser} onChange={set('maxClaimsPerUser')} />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Campaign start">
          <input type="datetime-local" className={inputCls} value={form.campaignStart} onChange={set('campaignStart')} />
        </Field>
        <Field label="Campaign end (optional)">
          <input type="datetime-local" className={inputCls} value={form.campaignEnd} onChange={set('campaignEnd')} />
        </Field>
        <Field label="Claim window start (optional)">
          <input type="datetime-local" className={inputCls} value={form.claimStart} onChange={set('claimStart')} />
        </Field>
        <Field label="Claim window end (optional)">
          <input type="datetime-local" className={inputCls} value={form.claimEnd} onChange={set('claimEnd')} />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={form.requireEmailVerified} onChange={set('requireEmailVerified')} className="w-4 h-4" />
          <span className="text-[12px] font-bold">Require verified email</span>
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={form.requireKyc} onChange={set('requireKyc')} className="w-4 h-4" />
          <span className="text-[12px] font-bold">Require KYC (not yet available)</span>
        </label>
      </div>

      <label className="flex items-center gap-2">
        <input type="checkbox" checked={form.autoCreditOnEligibility} onChange={set('autoCreditOnEligibility')} className="w-4 h-4" />
        <span className="text-[12px] font-bold">Credit CPT immediately once verified (recommended)</span>
      </label>

      <div className="flex gap-2 mt-1">
        <button onClick={onClose} disabled={loading}
          className="flex-1 px-4 py-2.5 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors disabled:opacity-50">
          Cancel
        </button>
        <button onClick={save} disabled={loading}
          className="flex-1 px-4 py-2.5 border-2 border-primary-container bg-primary-container text-on-primary-container font-bold text-[11px] uppercase tracking-widest hover:brightness-105 transition-colors disabled:opacity-50">
          {loading ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Campaign'}
        </button>
      </div>
    </Dialog>
  )
}

function StatsDialog({ campaignId, onClose }) {
  const [stats, setStats] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    adminGetCampaignStats(campaignId).then(setStats).catch(setError)
  }, [campaignId])

  return (
    <Dialog title="Campaign Stats" onClose={onClose}>
      {error && <AdminAlert type="error" message={error.message ?? 'Failed to load stats'} />}
      {!stats && !error ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-6" /><Skeleton className="h-6" /><Skeleton className="h-6" />
        </div>
      ) : stats && (
        <div className="flex flex-col gap-2 text-[12px]">
          <div className="flex justify-between"><span className="text-on-surface-variant">Total pool</span><span className="font-bold">{fmt(stats.totalPoolCpt)} CPT</span></div>
          <div className="flex justify-between"><span className="text-on-surface-variant">Distributed</span><span className="font-bold text-primary-container">{fmt(stats.poolClaimedCpt)} CPT</span></div>
          <div className="flex justify-between border-b-2 border-on-background/10 pb-2"><span className="text-on-surface-variant">Remaining</span><span className="font-bold">{fmt(stats.poolRemainingCpt)} CPT</span></div>
          <div className="flex justify-between"><span className="text-on-surface-variant">Eligible</span><span className="font-bold">{fmt(stats.claims?.eligible)}</span></div>
          <div className="flex justify-between"><span className="text-on-surface-variant">Claimed</span><span className="font-bold">{fmt(stats.claims?.claimed)}</span></div>
          <div className="flex justify-between"><span className="text-on-surface-variant">Rejected</span><span className="font-bold">{fmt(stats.claims?.rejected)}</span></div>
          <div className="flex justify-between"><span className="text-on-surface-variant">Blocked</span><span className="font-bold">{fmt(stats.claims?.blocked)}</span></div>
        </div>
      )}
    </Dialog>
  )
}

function PriceDialog({ campaign, onClose, onDone }) {
  const [price, setPrice] = useState(campaign.cptPriceUsd ?? '')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const submit = async () => {
    if (!price || Number(price) <= 0) { setError('Enter a valid CPT price'); return }
    setLoading(true)
    setError(null)
    try {
      await adminUpdateCampaignPrice(campaign.id, Number(price))
      onDone()
    } catch (err) {
      setError(err.message ?? 'Failed to update price')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog title="Update CPT Price" onClose={onClose}>
      <p className="text-[12px] text-on-surface-variant">
        Used to compute CPT rewards for <span className="font-bold text-on-surface">{campaign.title}</span> — there is
        no permanently fixed rate, so update this whenever the market price moves.
      </p>
      {error && <AdminAlert type="error" message={error} />}
      <Field label="Current CPT price (USD)">
        <input type="number" min="0.000001" step="any" className={inputCls} value={price} onChange={e => setPrice(e.target.value)} />
      </Field>
      <div className="flex gap-2 mt-1">
        <button onClick={onClose} disabled={loading}
          className="flex-1 px-4 py-2.5 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors disabled:opacity-50">
          Cancel
        </button>
        <button onClick={submit} disabled={loading}
          className="flex-1 px-4 py-2.5 border-2 border-primary-container bg-primary-container text-on-primary-container font-bold text-[11px] uppercase tracking-widest hover:brightness-105 transition-colors disabled:opacity-50">
          {loading ? 'Saving…' : 'Update Price'}
        </button>
      </div>
    </Dialog>
  )
}

function PayoutExportDialog({ campaign, onClose }) {
  const [csv, setCsv] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    adminGetPayoutExport(campaign.id).then(setCsv).catch(setError)
  }, [campaign.id])

  const download = () => {
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `bounty-payout-${campaign.id}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Dialog title="Payout Export" onClose={onClose} wide>
      <p className="text-[12px] text-on-surface-variant">
        Claimed-but-unpaid rewards for <span className="font-bold text-on-surface">{campaign.title}</span> —
        send these on-chain, then mark each claim paid with its transaction hash.
      </p>
      {error && <AdminAlert type="error" message={error.message ?? 'Failed to load export'} />}
      {csv == null && !error ? (
        <Skeleton className="h-32 w-full" />
      ) : csv && (
        <textarea readOnly rows={8} className={`${inputCls} font-mono resize-y`} value={csv} />
      )}
      <div className="flex gap-2 mt-1">
        <button onClick={onClose}
          className="flex-1 px-4 py-2.5 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors">
          Close
        </button>
        <button onClick={download} disabled={!csv}
          className="flex-1 px-4 py-2.5 border-2 border-primary-container bg-primary-container text-on-primary-container font-bold text-[11px] uppercase tracking-widest hover:brightness-105 transition-colors disabled:opacity-50">
          Download CSV
        </button>
      </div>
    </Dialog>
  )
}

function RejectClaimDialog({ claim, onClose, onDone }) {
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const submit = async () => {
    if (!reason.trim()) { setError('A reason is required'); return }
    setLoading(true)
    setError(null)
    try {
      await adminRejectClaim(claim.id, reason.trim())
      onDone()
    } catch (err) {
      setError(err.message ?? 'Failed to reject claim')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog title="Reject Claim" onClose={onClose}>
      <p className="text-[12px] text-on-surface-variant">
        If this claim was already credited, the CPT will be debited back from the user's balance.
      </p>
      {error && <AdminAlert type="error" message={error} />}
      <Field label="Reason">
        <textarea rows={3} className={`${inputCls} resize-y`} value={reason} onChange={e => setReason(e.target.value)}
          placeholder="e.g. Proof link doesn't match the campaign target post" />
      </Field>
      <div className="flex gap-2 mt-1">
        <button onClick={onClose} disabled={loading}
          className="flex-1 px-4 py-2.5 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors disabled:opacity-50">
          Cancel
        </button>
        <button onClick={submit} disabled={loading}
          className="flex-1 px-4 py-2.5 border-2 border-error bg-error text-on-error font-bold text-[11px] uppercase tracking-widest hover:opacity-90 transition-colors disabled:opacity-50">
          {loading ? 'Rejecting…' : 'Reject'}
        </button>
      </div>
    </Dialog>
  )
}

function MarkPaidDialog({ claim, onClose, onDone }) {
  const [txHash, setTxHash] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const submit = async () => {
    if (!txHash.trim()) { setError('A transaction hash is required'); return }
    setLoading(true)
    setError(null)
    try {
      await adminMarkClaimPaid(claim.id, txHash.trim())
      onDone()
    } catch (err) {
      setError(err.message ?? 'Failed to mark as paid')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog title="Mark Paid On-Chain" onClose={onClose}>
      <p className="text-[12px] text-on-surface-variant">
        Records the transaction hash once <span className="font-bold text-on-surface">{fmt(claim.cptAmount)} CPT</span> has
        actually been sent to <span className="font-mono text-on-surface">{claim.walletAddress}</span>.
      </p>
      {error && <AdminAlert type="error" message={error} />}
      <Field label="Transaction hash">
        <input className={`${inputCls} font-mono`} value={txHash} onChange={e => setTxHash(e.target.value)} placeholder="0x..." />
      </Field>
      <div className="flex gap-2 mt-1">
        <button onClick={onClose} disabled={loading}
          className="flex-1 px-4 py-2.5 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors disabled:opacity-50">
          Cancel
        </button>
        <button onClick={submit} disabled={loading}
          className="flex-1 px-4 py-2.5 border-2 border-primary-container bg-primary-container text-on-primary-container font-bold text-[11px] uppercase tracking-widest hover:brightness-105 transition-colors disabled:opacity-50">
          {loading ? 'Saving…' : 'Mark Paid'}
        </button>
      </div>
    </Dialog>
  )
}

function BanAccountDialog({ onClose, onDone }) {
  const [profileId, setProfileId] = useState('')
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const submit = async () => {
    if (!profileId.trim() || !reason.trim()) { setError('Profile ID and reason are both required'); return }
    setLoading(true)
    setError(null)
    try {
      await adminBanBountyAccount(profileId.trim(), reason.trim())
      onDone()
    } catch (err) {
      setError(err.message ?? 'Failed to ban account')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog title="Block Account from Bounty" onClose={onClose}>
      {error && <AdminAlert type="error" message={error} />}
      <Field label="Profile ID">
        <input className={inputCls} value={profileId} onChange={e => setProfileId(e.target.value)} />
      </Field>
      <Field label="Reason">
        <input className={inputCls} value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Confirmed multi-account farming" />
      </Field>
      <div className="flex gap-2 mt-1">
        <button onClick={onClose} disabled={loading}
          className="flex-1 px-4 py-2.5 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors disabled:opacity-50">
          Cancel
        </button>
        <button onClick={submit} disabled={loading}
          className="flex-1 px-4 py-2.5 border-2 border-error bg-error text-on-error font-bold text-[11px] uppercase tracking-widest hover:opacity-90 transition-colors disabled:opacity-50">
          {loading ? 'Blocking…' : 'Block'}
        </button>
      </div>
    </Dialog>
  )
}

function CampaignRow({ campaign, onEdit, onPrice, onActivate, onPause, onEnd, onStats, onExport, actingOn }) {
  const busy = actingOn === campaign.id
  return (
    <div className="border-4 border-on-background bg-surface p-4 flex items-center justify-between gap-3 flex-wrap"
      style={{ boxShadow: '4px 4px 0px 0px var(--neo-border-color)' }}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="px-2 py-0.5 border-2 border-on-background/30 font-bold text-[10px] uppercase tracking-widest">{campaign.taskType}</span>
          <span className={`px-2 py-0.5 border-2 font-bold text-[10px] uppercase tracking-widest ${
            campaign.status === 'active' ? 'border-primary-container text-on-primary-container bg-primary-container/15'
            : campaign.status === 'paused' ? 'border-on-background/30 text-on-surface-variant'
            : campaign.status === 'ended' ? 'border-error text-error'
            : 'border-on-background/30 text-on-surface-variant'
          }`}>{campaign.status}</span>
          <span className="px-2 py-0.5 border-2 border-on-background/30 font-bold text-[10px] uppercase tracking-widest">
            {campaign.rewardMode === 'usd_value' ? `$${campaign.rewardValueUsd} @ $${campaign.cptPriceUsd}/CPT` : `${campaign.rewardAmountCpt} CPT`}
          </span>
        </div>
        <p className="font-bold text-[14px]">{campaign.title}</p>
        <p className="text-[11px] text-on-surface-variant truncate max-w-lg">{campaign.description}</p>
        <p className="text-[11px] text-on-surface-variant font-mono mt-1">
          {fmt(campaign.poolClaimedCpt)} / {fmt(campaign.totalPoolCpt)} CPT distributed
        </p>
      </div>
      <div className="flex gap-2 shrink-0 flex-wrap">
        <button onClick={() => onStats(campaign)}
          className="px-3 py-2 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-surface-container transition-colors">
          Stats
        </button>
        {campaign.rewardMode === 'usd_value' && (
          <button onClick={() => onPrice(campaign)}
            className="px-3 py-2 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-surface-container transition-colors">
            Price
          </button>
        )}
        <button onClick={() => onExport(campaign)}
          className="px-3 py-2 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-surface-container transition-colors">
          Export
        </button>
        <button onClick={() => onEdit(campaign)}
          className="px-3 py-2 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-surface-container transition-colors">
          Edit
        </button>
        {campaign.status !== 'active' && campaign.status !== 'ended' && (
          <button onClick={() => onActivate(campaign)} disabled={busy}
            className="px-3 py-2 border-2 border-primary-container bg-primary-container text-on-primary-container font-bold text-[10px] uppercase tracking-widest hover:brightness-105 transition-colors disabled:opacity-40">
            Activate
          </button>
        )}
        {campaign.status === 'active' && (
          <button onClick={() => onPause(campaign)} disabled={busy}
            className="px-3 py-2 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-surface-container transition-colors disabled:opacity-40">
            Pause
          </button>
        )}
        {campaign.status !== 'ended' && (
          <button onClick={() => onEnd(campaign)} disabled={busy}
            className="px-3 py-2 border-2 border-error text-error font-bold text-[10px] uppercase tracking-widest hover:bg-error hover:text-on-error transition-colors disabled:opacity-40">
            End
          </button>
        )}
      </div>
    </div>
  )
}

function ClaimRow({ claim, onReject, onMarkPaid, actingOn }) {
  const busy = actingOn === claim.id
  return (
    <div className="border-4 border-on-background bg-surface p-4 flex items-center justify-between gap-3 flex-wrap"
      style={{ boxShadow: '4px 4px 0px 0px var(--neo-border-color)' }}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className={`px-2 py-0.5 border-2 font-bold text-[10px] uppercase tracking-widest ${
            claim.status === 'claimed' ? 'border-primary-container text-on-primary-container bg-primary-container/15'
            : claim.status === 'rejected' || claim.status === 'blocked' ? 'border-error text-error'
            : 'border-on-background/30 text-on-surface-variant'
          }`}>{claim.status}</span>
          {claim.paidAt && (
            <span className="px-2 py-0.5 border-2 border-on-background/30 font-bold text-[10px] uppercase tracking-widest">Paid on-chain</span>
          )}
        </div>
        <p className="text-[11px] text-on-surface-variant font-mono truncate">Profile: {claim.profileId}</p>
        <p className="text-[11px] text-on-surface-variant font-mono truncate">
          Wallet: {claim.walletAddress || 'None on file yet (referral — not wired up)'}
        </p>
        {claim.proofUrl && (
          <a href={claim.proofUrl} target="_blank" rel="noopener noreferrer"
            className="text-[11px] font-bold text-primary-container hover:underline truncate block">
            {claim.proofUrl}
          </a>
        )}
        {claim.rejectionReason && (
          <p className="text-[11px] text-error mt-1">Reason: {claim.rejectionReason}</p>
        )}
        <p className="text-[11px] text-on-surface-variant font-mono mt-1">
          {fmt(claim.cptAmount)} CPT · {claim.createdAt ? new Date(claim.createdAt).toLocaleString() : ''}
        </p>
      </div>
      <div className="flex gap-2 shrink-0">
        {claim.status === 'claimed' && !claim.paidAt && claim.walletAddress && (
          <button onClick={() => onMarkPaid(claim)} disabled={busy}
            className="px-3 py-2 border-2 border-primary-container bg-primary-container text-on-primary-container font-bold text-[10px] uppercase tracking-widest hover:brightness-105 transition-colors disabled:opacity-40">
            Mark Paid
          </button>
        )}
        {(claim.status === 'eligible' || claim.status === 'claimed') && (
          <button onClick={() => onReject(claim)} disabled={busy}
            className="px-3 py-2 border-2 border-error text-error font-bold text-[10px] uppercase tracking-widest hover:bg-error hover:text-on-error transition-colors disabled:opacity-40">
            Reject
          </button>
        )}
      </div>
    </div>
  )
}

function FlagRow({ flag, onUnban }) {
  return (
    <div className="border-4 border-on-background bg-surface p-4 flex items-center justify-between gap-3 flex-wrap"
      style={{ boxShadow: '4px 4px 0px 0px var(--neo-border-color)' }}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className={`px-2 py-0.5 border-2 font-bold text-[10px] uppercase tracking-widest ${
            flag.bannedBy === 'system' ? 'border-error text-error' : 'border-on-background/30 text-on-surface-variant'
          }`}>{flag.bannedBy === 'system' ? 'Auto-blocked' : 'Manually blocked'}</span>
        </div>
        <p className="text-[11px] text-on-surface-variant font-mono truncate">Profile: {flag.profileId}</p>
        {flag.walletAddress && <p className="text-[11px] text-on-surface-variant font-mono truncate">Wallet: {flag.walletAddress}</p>}
        {flag.ipAddress && <p className="text-[11px] text-on-surface-variant font-mono truncate">IP: {flag.ipAddress}</p>}
        <p className="text-[11px] mt-1">{flag.reason}</p>
      </div>
      <button onClick={() => onUnban(flag)}
        className="px-3 py-2 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-surface-container transition-colors shrink-0">
        Unblock
      </button>
    </div>
  )
}

export function BountyManagement() {
  const [view, setView] = useState('campaigns') // 'campaigns' | 'claims' | 'flags'

  const [campaignStatus, setCampaignStatus] = useState(undefined)
  const [campaigns, setCampaigns] = useState([])
  const [campaignsLoading, setCampaignsLoading] = useState(true)
  const [campaignsError, setCampaignsError] = useState(null)

  const [claimStatus, setClaimStatus] = useState(undefined)
  const [claims, setClaims] = useState([])
  const [claimsLoading, setClaimsLoading] = useState(true)
  const [claimsError, setClaimsError] = useState(null)

  const [flags, setFlags] = useState([])
  const [flagsLoading, setFlagsLoading] = useState(true)
  const [flagsError, setFlagsError] = useState(null)

  const [formCampaign, setFormCampaign] = useState(null)
  const [statsCampaign, setStatsCampaign] = useState(null)
  const [priceCampaign, setPriceCampaign] = useState(null)
  const [exportCampaign, setExportCampaign] = useState(null)
  const [rejectClaim, setRejectClaim] = useState(null)
  const [payClaim, setPayClaim] = useState(null)
  const [showBanDialog, setShowBanDialog] = useState(false)
  const [actingOn, setActingOn] = useState(null)
  const [actionError, setActionError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)

  const loadCampaigns = useCallback(() => {
    setCampaignsLoading(true)
    setCampaignsError(null)
    adminGetCampaigns(campaignStatus).then(setCampaigns).catch(setCampaignsError).finally(() => setCampaignsLoading(false))
  }, [campaignStatus])

  const loadClaims = useCallback(() => {
    setClaimsLoading(true)
    setClaimsError(null)
    adminGetClaims({ status: claimStatus }).then(setClaims).catch(setClaimsError).finally(() => setClaimsLoading(false))
  }, [claimStatus])

  const loadFlags = useCallback(() => {
    setFlagsLoading(true)
    setFlagsError(null)
    adminGetBountyFlags().then(setFlags).catch(setFlagsError).finally(() => setFlagsLoading(false))
  }, [])

  useEffect(() => { loadCampaigns() }, [loadCampaigns])
  useEffect(() => { loadClaims() }, [loadClaims])
  useEffect(() => { loadFlags() }, [loadFlags])

  if (campaignsError && isNotBuiltYet(campaignsError) && campaigns.length === 0 && !campaignsLoading) {
    return <AdminComingSoon label="Bounty Campaigns" />
  }

  const flash = (msg) => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(null), 3000) }

  const doActivate = async (c) => {
    setActingOn(c.id); setActionError(null)
    try { await adminActivateCampaign(c.id); flash('Campaign activated — banner posted.'); loadCampaigns() }
    catch (err) { setActionError(err.message ?? 'Failed to activate campaign') }
    finally { setActingOn(null) }
  }
  const doPause = async (c) => {
    setActingOn(c.id); setActionError(null)
    try { await adminPauseCampaign(c.id); flash('Campaign paused.'); loadCampaigns() }
    catch (err) { setActionError(err.message ?? 'Failed to pause campaign') }
    finally { setActingOn(null) }
  }
  const doEnd = async (c) => {
    setActingOn(c.id); setActionError(null)
    try { await adminEndCampaign(c.id); flash('Campaign ended.'); loadCampaigns() }
    catch (err) { setActionError(err.message ?? 'Failed to end campaign') }
    finally { setActingOn(null) }
  }
  const doUnban = async (flag) => {
    setActingOn(flag.profileId); setActionError(null)
    try { await adminUnbanBountyAccount(flag.profileId); flash('Account unblocked.'); loadFlags() }
    catch (err) { setActionError(err.message ?? 'Failed to unblock account') }
    finally { setActingOn(null) }
  }

  return (
    <div className="flex flex-col gap-6">
      {formCampaign !== null && (
        <CampaignFormDialog
          campaign={formCampaign.id ? formCampaign : null}
          onClose={() => setFormCampaign(null)}
          onSaved={() => { setFormCampaign(null); flash(formCampaign.id ? 'Campaign updated.' : 'Campaign created.'); loadCampaigns() }}
        />
      )}
      {statsCampaign && <StatsDialog campaignId={statsCampaign.id} onClose={() => setStatsCampaign(null)} />}
      {priceCampaign && (
        <PriceDialog campaign={priceCampaign} onClose={() => setPriceCampaign(null)}
          onDone={() => { setPriceCampaign(null); flash('CPT price updated.'); loadCampaigns() }} />
      )}
      {exportCampaign && <PayoutExportDialog campaign={exportCampaign} onClose={() => setExportCampaign(null)} />}
      {rejectClaim && (
        <RejectClaimDialog claim={rejectClaim} onClose={() => setRejectClaim(null)}
          onDone={() => { setRejectClaim(null); flash('Claim rejected.'); loadClaims(); loadCampaigns() }} />
      )}
      {payClaim && (
        <MarkPaidDialog claim={payClaim} onClose={() => setPayClaim(null)}
          onDone={() => { setPayClaim(null); flash('Claim marked as paid.'); loadClaims() }} />
      )}
      {showBanDialog && (
        <BanAccountDialog onClose={() => setShowBanDialog(false)}
          onDone={() => { setShowBanDialog(false); flash('Account blocked.'); loadFlags() }} />
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1 border-2 border-on-background w-fit">
          <button onClick={() => setView('campaigns')}
            className={`px-4 py-2 font-bold text-[11px] uppercase tracking-widest transition-colors ${view === 'campaigns' ? 'bg-on-background text-surface' : 'bg-surface hover:bg-surface-container text-on-surface'}`}>
            Campaigns
          </button>
          <button onClick={() => setView('claims')}
            className={`px-4 py-2 font-bold text-[11px] uppercase tracking-widest transition-colors ${view === 'claims' ? 'bg-on-background text-surface' : 'bg-surface hover:bg-surface-container text-on-surface'}`}>
            Claims
          </button>
          <button onClick={() => setView('flags')}
            className={`px-4 py-2 font-bold text-[11px] uppercase tracking-widest transition-colors ${view === 'flags' ? 'bg-on-background text-surface' : 'bg-surface hover:bg-surface-container text-on-surface'}`}>
            Fraud Flags
          </button>
        </div>
        {view === 'campaigns' && (
          <button onClick={() => setFormCampaign({})}
            className="px-4 py-2 border-2 border-primary-container bg-primary-container text-on-primary-container font-bold text-[11px] uppercase tracking-widest hover:brightness-105 transition-colors">
            + New Campaign
          </button>
        )}
        {view === 'flags' && (
          <button onClick={() => setShowBanDialog(true)}
            className="px-4 py-2 border-2 border-error text-error font-bold text-[11px] uppercase tracking-widest hover:bg-error hover:text-on-error transition-colors">
            + Block Account
          </button>
        )}
      </div>

      {successMsg && <AdminAlert type="success" message={successMsg} />}
      {actionError && <AdminAlert type="error" message={actionError} />}

      {view === 'campaigns' && (
        <div className="flex flex-col gap-4">
          <div className="flex gap-1 border-2 border-on-background w-fit">
            {CAMPAIGN_STATUS_FILTERS.map(f => (
              <button key={f.label} onClick={() => setCampaignStatus(f.key)}
                className={`px-3 py-1.5 font-bold text-[11px] uppercase tracking-widest transition-colors ${
                  campaignStatus === f.key ? 'bg-on-background text-surface' : 'bg-surface hover:bg-surface-container text-on-surface'
                }`}>
                {f.label}
              </button>
            ))}
          </div>
          {campaignsError && !isNotBuiltYet(campaignsError) && (
            <AdminAlert type="error" message={campaignsError.message ?? 'Failed to load campaigns'} />
          )}
          {campaignsLoading ? (
            <div className="flex flex-col gap-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}</div>
          ) : campaigns.length === 0 ? (
            <div className="border-4 border-dashed border-on-background/25 bg-surface-container-low p-8 text-center">
              <p className="text-on-surface-variant font-bold text-[12px] uppercase tracking-widest">No campaigns match these filters</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {campaigns.map(c => (
                <CampaignRow key={c.id} campaign={c}
                  onEdit={setFormCampaign} onPrice={setPriceCampaign} onActivate={doActivate}
                  onPause={doPause} onEnd={doEnd} onStats={setStatsCampaign} onExport={setExportCampaign}
                  actingOn={actingOn} />
              ))}
            </div>
          )}
        </div>
      )}

      {view === 'claims' && (
        <div className="flex flex-col gap-4">
          <div className="flex gap-1 border-2 border-on-background w-fit">
            {CLAIM_STATUS_FILTERS.map(f => (
              <button key={f.label} onClick={() => setClaimStatus(f.key)}
                className={`px-3 py-1.5 font-bold text-[11px] uppercase tracking-widest transition-colors ${
                  claimStatus === f.key ? 'bg-on-background text-surface' : 'bg-surface hover:bg-surface-container text-on-surface'
                }`}>
                {f.label}
              </button>
            ))}
          </div>
          {claimsError && !isNotBuiltYet(claimsError) && (
            <AdminAlert type="error" message={claimsError.message ?? 'Failed to load claims'} />
          )}
          {claimsLoading ? (
            <div className="flex flex-col gap-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}</div>
          ) : claims.length === 0 ? (
            <div className="border-4 border-dashed border-on-background/25 bg-surface-container-low p-8 text-center">
              <p className="text-on-surface-variant font-bold text-[12px] uppercase tracking-widest">No claims match these filters</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {claims.map(c => (
                <ClaimRow key={c.id} claim={c} onReject={setRejectClaim} onMarkPaid={setPayClaim} actingOn={actingOn} />
              ))}
            </div>
          )}
        </div>
      )}

      {view === 'flags' && (
        <div className="flex flex-col gap-4">
          {flagsError && !isNotBuiltYet(flagsError) && (
            <AdminAlert type="error" message={flagsError.message ?? 'Failed to load flags'} />
          )}
          {flagsLoading ? (
            <div className="flex flex-col gap-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}</div>
          ) : flags.length === 0 ? (
            <div className="border-4 border-dashed border-on-background/25 bg-surface-container-low p-8 text-center">
              <p className="text-on-surface-variant font-bold text-[12px] uppercase tracking-widest">No accounts are currently blocked</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {flags.map(f => <FlagRow key={f.id} flag={f} onUnban={doUnban} />)}
            </div>
          )}
        </div>
      )}
    </div>
  )
}